type NameItem = string | { name: string }

interface FormatNameListOptions {
  /**
   * The conjunction word to use before the last item
   * @default 'dan'
   */
  conjunction?: string
  /**
   * The separator to use between items
   * @default ', '
   */
  separator?: string
}

/**
 * Formats a list of names into a grammatically correct string with proper conjunctions.
 *
 * @example
 * ```ts
 * formatNameList(['John', 'Jane', 'Bob'])
 * // Returns: "John, Jane, dan Bob"
 *
 * formatNameList([{ name: 'Alice' }, { name: 'Charlie' }])
 * // Returns: "Alice, dan Charlie"
 *
 * formatNameList(['Single'])
 * // Returns: "Single"
 *
 * formatNameList(['A', 'B'], { conjunction: 'and' })
 * // Returns: "A, and B"
 * ```
 *
 * @param items - Array of names (strings or objects with name property)
 * @param options - Formatting options
 * @returns Formatted name list string
 */
export function formatNameList(items: NameItem[], options: FormatNameListOptions = {}): string {
  const { conjunction = 'dan', separator = ', ' } = options

  if (items.length === 0) return ''
  if (items.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstItem = items[0]!
    return typeof firstItem === 'object' ? firstItem.name : firstItem
  }

  return items
    .map((item, index, array) => {
      const name = typeof item === 'object' ? item.name : item
      const isLast = index === array.length - 1
      const isSecondToLast = index === array.length - 2

      if (isLast) return name
      if (isSecondToLast) return `${name}${separator}${conjunction} `
      return `${name}${separator}`
    })
    .join('')
}
